import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { scrollToSection } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  "Excavation / Site Work",
  "Concrete",
  "Renovation",
  "New Construction",
  "Commercial Construction",
  "Landscape / Property Improvement",
  "Real Estate Development",
  "Other",
];

const budgets = [
  "Under $25,000",
  "$25,000 – $75,000",
  "$75,000 – $150,000",
  "$150,000 – $500,000",
  "$500,000+",
  "Prefer to Discuss",
];

const emptyForm = { name: "", phone: "", email: "", project_address: "", service: "", budget: "", start_date: "", message: "" };

const inputCls =
  "w-full bg-[#111111] border border-white/10 focus:border-[#D4AF37]/60 px-4 py-3.5 text-sm font-light tracking-wide text-[#F5F5F5] placeholder:text-[#A3A3A3]/60 outline-none transition-colors duration-300";

const details = [
  { icon: Phone, label: "Call or Text", value: "816-405-6181", href: "tel:+18164056181", testid: "contact-phone-link" },
  { icon: Mail, label: "Email", value: "info@veritasdevelopmentgroupllc.com", href: "mailto:info@veritasdevelopmentgroupllc.com", testid: "contact-email-link" },
  { icon: MapPin, label: "Serving", value: "Lee's Summit, Missouri & surrounding communities", href: null, testid: "contact-location" },
  { icon: Clock, label: "Consultations", value: "Free — by appointment", href: null, testid: "contact-hours" },
];

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const onPreselect = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("veritas:project-type", onPreselect);
    return () => window.removeEventListener("veritas:project-type", onPreselect);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request received — we'll call you back shortly.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please call us at 816-405-6181.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-40 border-t border-[#D4AF37]/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-8"
            data-testid="contact-overline"
          >
            04 — Request a Free Consultation
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#F5F5F5]"
            data-testid="contact-headline"
          >
            Let's break
            <br />
            <span className="italic text-[#D4AF37]">ground.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-md text-base font-light leading-relaxed tracking-wide text-[#A3A3A3]"
          >
            Request a Consultation and explore our services.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-10 flex flex-col gap-3.5"
            data-testid="contact-ctas"
          >
            <button
              onClick={() => scrollToSection("#contact-form")}
              data-testid="cta-construction-project"
              className="w-full bg-[#D4AF37] text-black px-8 py-4 text-[10px] tracking-[0.28em] uppercase font-medium hover:bg-[#F3E5AB] transition-colors duration-300"
            >
              Start a Construction Project
            </button>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent("veritas:project-type", { detail: "Real Estate Development" }));
                scrollToSection("#contact-form");
              }}
              data-testid="cta-development-opportunity"
              className="w-full border border-[#D4AF37]/40 text-[#D4AF37] px-8 py-4 text-[10px] tracking-[0.28em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
            >
              Submit a Development Opportunity
            </button>
            <button
              onClick={() => navigate("/financing", { state: { scrollTo: "#funding-form" } })}
              data-testid="cta-request-financing"
              className="w-full border border-[#D4AF37]/40 text-[#D4AF37] px-8 py-4 text-[10px] tracking-[0.28em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
            >
              Request Financing
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-12 space-y-7"
            data-testid="contact-details"
          >
            {details.map((d) => {
              const Icon = d.icon;
              const inner = (
                <>
                  <span className="inline-flex items-center justify-center w-11 h-11 border border-[#D4AF37]/35 text-[#D4AF37] shrink-0">
                    <Icon size={17} strokeWidth={1.25} />
                  </span>
                  <span>
                    <span className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{d.label}</span>
                    <span className="block mt-1 text-base sm:text-lg font-light tracking-wide text-[#F5F5F5]">{d.value}</span>
                  </span>
                </>
              );
              return d.href ? (
                <a key={d.label} href={d.href} data-testid={d.testid} className="flex items-center gap-5 group">
                  <span className="contents group-hover:[&>span:first-child]:bg-[#D4AF37]">{inner}</span>
                </a>
              ) : (
                <div key={d.label} data-testid={d.testid} className="flex items-center gap-5">
                  {inner}
                </div>
              );
            })}
          </motion.div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.15 }}
          onSubmit={submit}
          id="contact-form"
          className="relative border border-[#D4AF37]/20 bg-[#0A0A0A] p-8 sm:p-12 h-fit"
          data-testid="contact-form"
        >
          <span className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37] -translate-x-px -translate-y-px" aria-hidden="true" />
          <div className="space-y-6">
            <div>
              <label htmlFor="lead-name" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Name *</label>
              <input id="lead-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="lead-phone" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Phone</label>
                <input id="lead-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(816) 000-0000" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-email" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Email</label>
                <input id="lead-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="lead-address" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Project Address</label>
              <input id="lead-address" data-testid="contact-address-input" value={form.project_address} onChange={set("project_address")} placeholder="Street, city, or area of the project" className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="lead-service" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Project Type</label>
                <select id="lead-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled>Select a project type</option>
                  {projectTypes.map((s) => (
                    <option key={s} value={s} className="bg-[#111111]">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="lead-budget" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Estimated Budget</label>
                <select id="lead-budget" data-testid="contact-budget-select" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled>Select a range</option>
                  {budgets.map((b) => (
                    <option key={b} value={b} className="bg-[#111111]">{b}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="lead-start" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Desired Start Date</label>
              <input id="lead-start" data-testid="contact-start-input" type="date" value={form.start_date} onChange={set("start_date")} className={`${inputCls} [color-scheme:dark]`} />
            </div>
            <div>
              <label htmlFor="lead-message" className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5">Brief Project Description</label>
              <textarea id="lead-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your project..." className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full bg-[#D4AF37] text-black py-4 text-[11px] tracking-[0.3em] uppercase font-medium hover:bg-[#F3E5AB] disabled:opacity-60 transition-colors duration-300"
            >
              {sending ? "Sending..." : "Request Project Estimate"}
            </button>
            <p className="text-center text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]/70">
              Or call today — <a href="tel:+18164056181" data-testid="contact-form-phone-link" className="text-[#D4AF37]">816-405-6181</a>
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
